import { useEffect, useState } from "react";
import { Bell, X, Smartphone } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const STORAGE_KEY = "hd_push_onboarding_v1";
const DELAY_MS = 4000; // Vänta lite innan vi visar - inte direkt vid login

/**
 * Onboarding-popup som frågar om push-notiser EN gång per enhet.
 *
 * Visas för:
 *  - Nya användare efter registrering
 *  - Befintliga användare när de loggar in från en enhet utan push
 *
 * Visas INTE om:
 *  - Användaren redan svarat (sparat i localStorage)
 *  - Webbläsaren inte stöder push
 *  - Användaren redan är prenumererad
 *  - Tillstånd är "denied" (kan inte fråga igen ändå)
 *  - Vi är i Lovable preview-iframe (sw kan inte registreras)
 */
export const PushOnboardingDialog = () => {
  const { isSupported, permission, isSubscribed, subscribe, loading } =
    usePushNotifications();
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  // Lyssna på auth state
  useEffect(() => {
    let mounted = true;

    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (mounted) {
        setUserId(user?.id ?? null);
        setChecked(true);
      }
    };
    checkUser();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setUserId(session?.user?.id ?? null);
        setChecked(true);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Avgör om vi ska visa popup
  useEffect(() => {
    if (!checked || !userId) return;
    if (!isSupported) return;
    if (isSubscribed) return;
    if (permission === "denied") return;

    // Lovable preview iframe → skippa
    if (typeof window !== "undefined") {
      const isPreview = window.location.hostname.includes("lovableproject.com") ||
                        window.location.hostname.includes("lovable.app") &&
                        window.location.hostname.includes("id-preview");
      if (isPreview) return;
    }

    // Per-användare nyckel så att olika konton på samma enhet får var sin fråga
    const storageKey = `${STORAGE_KEY}_${userId}`;
    const alreadyAnswered = localStorage.getItem(storageKey);
    if (alreadyAnswered) return;

    const timer = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, [checked, userId, isSupported, isSubscribed, permission]);

  const markAnswered = (choice: "yes" | "no" | "later") => {
    if (!userId) return;
    localStorage.setItem(`${STORAGE_KEY}_${userId}`, choice);
  };

  const handleEnable = async () => {
    const ok = await subscribe();
    markAnswered(ok ? "yes" : "no");
    setOpen(false);
  };

  const handleSkip = () => {
    markAnswered("no");
    setOpen(false);
  };

  const isIOS = typeof window !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone =
    typeof window !== "undefined" &&
    window.matchMedia?.("(display-mode: standalone)").matches;
  const iosNotInstalled = isIOS && !isStandalone;

  if (iosNotInstalled) {
    // På iPhone Safari fungerar push bara från hemskärmen – visa annan typ av popup
    // men endast en gång
    // (Hanteras separat – skippa här för att inte skapa dålig UX)
    return null;
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Bell className="h-7 w-7 text-primary" />
          </div>
          <AlertDialogTitle className="text-center">
            Vill du få push-notiser?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center space-y-2">
            <span className="block">
              Få meddelande direkt på din enhet när:
            </span>
            <span className="block text-sm text-foreground">
              💬 Någon skickar dig ett meddelande<br />
              🎉 Din annons säljs<br />
              📦 Din vara skickas<br />
              🆕 Bästa nya träff på dina sparade sökningar
            </span>
            <span className="block text-xs pt-2">
              Du kan stänga av notiser när som helst i Inställningar.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <AlertDialogCancel onClick={handleSkip} disabled={loading}>
            Inte nu
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleEnable} disabled={loading}>
            <Bell className="mr-2 h-4 w-4" />
            {loading ? "Aktiverar…" : "Aktivera notiser"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
